import type { ReadonlyRecord } from '../../utils'

import type {
    ClientRegistryAuthorizationRequest,
    ClientRegistryData,
    ConnectwareError,
    CybusPermission,
    CybusRole,
    CybusUser,
    PaginatedData,
    RoleCreationRequest,
    RoleEditingRequest,
    UserCreationRequest,
    UserEditingRequest,
    UserManagementConfiguration,
} from '../../domain'

export type PasswordChangeRequest = ReadonlyRecord<'password' | 'newPassword', string>

export interface UserService {
    /**
     * @throws {ConnectwareError}
     */
    fetchRolesPage(search: string, showInternal: boolean, page: number): Promise<PaginatedData<CybusRole>>

    /**
     * @throws {ConnectwareError}
     */
    fetchRoles(search: string): Promise<CybusRole[]>

    /**
     * @throws {ConnectwareError}
     */
    fetchRole(roleName: CybusRole['name']): Promise<CybusRole>

    /**
     * @throws {ConnectwareError}
     */
    createRole(form: RoleCreationRequest): Promise<void>

    /**
     * @throws {ConnectwareError}
     */
    updateRole(form: RoleEditingRequest): Promise<void>

    /**
     * @throws {ConnectwareError}
     */
    deleteRole(id: CybusRole['id']): Promise<void>

    /**
     * @throws {ConnectwareError}
     */
    fetchUsersPage(search: string, showInternal: boolean, page: number): Promise<PaginatedData<CybusUser>>

    /**
     * @throws {ConnectwareError}
     */
    fetchUsers(search: string): Promise<CybusUser[]>

    /**
     * @throws {ConnectwareError}
     */
    fetchUser(username: CybusUser['username']): Promise<CybusUser>

    /**
     * @throws {ConnectwareError}
     */
    createUser(user: UserCreationRequest): Promise<void>

    /**
     * @throws {ConnectwareError}
     */
    updateUser(user: UserEditingRequest): Promise<void>

    /**
     * @throws {ConnectwareError}
     */
    deleteUser(id: CybusUser['id']): Promise<void>

    /**
     * @throws {ConnectwareError}
     */
    fetchPermissionsPage(search: string, showInternal: boolean, page: number): Promise<PaginatedData<CybusPermission>>

    /**
     * @throws {ConnectwareError}
     */
    changePassword(request: PasswordChangeRequest): Promise<void>

    /**
     * @throws {ConnectwareError} only if there is a major issue
     * Yields password validation errors {ConnectwareError} or confirmations {string}
     */
    validatePassword(password: string): Promise<(string | ConnectwareError)[]>

    /**
     * @throws {ConnectwareError} only if there is a major issue
     * Yields username validation errors {ConnectwareError} or confirmations {string}
     */
    validateUsername(username: CybusUser['username']): Promise<(string | ConnectwareError)[]>

    /**
     * @throws {ConnectwareError}
     */
    fetchManagementConfiguration(): Promise<UserManagementConfiguration>
}

export interface ClientRegistryService {
    /**
     * @throws {ConnectwareError}
     */
    fetchData(): Promise<ClientRegistryData>

    /**
     * @throws {ConnectwareError}
     */
    lock(): Promise<void>

    /**
     * @throws {ConnectwareError}
     */
    unlock(): Promise<void>

    /**
     * @throws {ConnectwareError}
     */
    authorize(request: ClientRegistryAuthorizationRequest): Promise<void>
}
